// src/components/ui/CookieConsent/CookieConsent.tsx
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X, Check, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';

const Banner = styled(motion.div)`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10000;
  background: white;
  box-shadow: 0 -10px 40px rgba(0, 0, 0, 0.15);
  border-top: 3px solid ${props => props.theme.colors.secondary};
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 32px 20px;
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 32px;
  align-items: center;

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    gap: 24px;
    text-align: center;
  }
`;

const IconWrapper = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, ${props => props.theme.colors.secondary} 0%, ${props => props.theme.colors.accent} 100%);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    margin: 0 auto;
  }
`;

const Content = styled.div``;

const Title = styled.h3`
  font-size: 1.25rem;
  font-family: ${props => props.theme.fonts.primary};
  color: ${props => props.theme.colors.primary};
  margin-bottom: 8px;
  font-weight: 700;
`;

const Description = styled.p`
  font-size: 1rem;
  color: ${props => props.theme.colors.gray[600]};
  line-height: 1.6;

  a {
    color: ${props => props.theme.colors.secondary};
    text-decoration: underline;
    transition: color 0.3s ease;

    &:hover {
      color: ${props => props.theme.colors.accent};
    }
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    justify-content: center;
    flex-wrap: wrap;
  }

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    flex-direction: column;
    width: 100%;
  }
`;

const Button = styled.button<{ $primary?: boolean }>`
  padding: 14px 28px;
  background: ${props => props.$primary 
    ? `linear-gradient(135deg, ${props.theme.colors.primary} 0%, ${props.theme.colors.accent} 100%)`
    : 'transparent'
  };
  color: ${props => props.$primary ? 'white' : props.theme.colors.primary};
  border: 2px solid ${props => props.$primary ? 'transparent' : props.theme.colors.gray[300]};
  border-radius: 50px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px ${props => props.$primary 
      ? 'rgba(26, 58, 82, 0.3)' 
      : 'rgba(0, 0, 0, 0.1)'
    };
    ${props => !props.$primary && `
      background: ${props.theme.colors.light};
      border-color: ${props.theme.colors.secondary};
    `}
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    justify-content: center;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 32px;
  height: 32px;
  background: none;
  border: none;
  color: ${props => props.theme.colors.gray[500]};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.theme.colors.gray[200]};
    color: ${props => props.theme.colors.primary};
  }

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    display: none;
  }
`;

// Preferences Modal
const ModalOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 10001;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const Modal = styled(motion.div)`
  background: white;
  border-radius: 24px;
  max-width: 600px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  padding: 32px;
  border-bottom: 1px solid ${props => props.theme.colors.gray[200]};
  position: relative;
`;

const ModalTitle = styled.h2`
  font-size: 1.75rem;
  font-family: ${props => props.theme.fonts.primary};
  color: ${props => props.theme.colors.primary};
  margin-bottom: 8px;
  font-weight: 700;
`;

const ModalSubtitle = styled.p`
  color: ${props => props.theme.colors.gray[600]};
  font-size: 1rem;
`;

const ModalClose = styled.button`
  position: absolute;
  top: 32px;
  right: 32px;
  width: 36px;
  height: 36px;
  background: ${props => props.theme.colors.gray[200]};
  border: none;
  border-radius: 50%;
  color: ${props => props.theme.colors.gray[600]};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.theme.colors.gray[300]};
    color: ${props => props.theme.colors.primary};
  }
`;

const ModalBody = styled.div`
  padding: 32px;
`;

const PreferenceItem = styled.div`
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid ${props => props.theme.colors.gray[200]};

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

const PreferenceHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const PreferenceTitle = styled.h4`
  font-size: 1.125rem;
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
`;

const Toggle = styled.button<{ $active: boolean; $disabled?: boolean }>`
  width: 56px;
  height: 28px;
  background: ${props => props.$active ? props.theme.colors.secondary : props.theme.colors.gray[300]};
  border: none;
  border-radius: 14px;
  position: relative;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: background 0.3s ease;
  opacity: ${props => props.$disabled ? 0.5 : 1};

  &::after {
    content: '';
    position: absolute;
    top: 3px;
    left: ${props => props.$active ? '31px' : '3px'};
    width: 22px;
    height: 22px;
    background: white;
    border-radius: 50%;
    transition: left 0.3s ease;
  }
`;

const PreferenceDescription = styled.p`
  color: ${props => props.theme.colors.gray[600]};
  font-size: 0.95rem;
  line-height: 1.6;
`;

const ModalFooter = styled.div`
  padding: 24px 32px;
  border-top: 1px solid ${props => props.theme.colors.gray[200]};
  display: flex;
  gap: 12px;
  justify-content: flex-end;

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    flex-direction: column;
  }
`;

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

const CookieConsent: React.FC = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Show banner after a short delay for better UX
      setTimeout(() => setShowBanner(true), 1000);
    }
  }, []);

  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
    };
    localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
    setShowBanner(false);
  };

  const acceptNecessary = () => {
    const necessaryOnly = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    localStorage.setItem('cookieConsent', JSON.stringify(necessaryOnly));
    setShowBanner(false);
  };

  const savePreferences = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(preferences));
    setShowModal(false);
    setShowBanner(false);
  };

  const togglePreference = (key: keyof CookiePreferences) => {
    if (key === 'necessary') return; // Can't disable necessary cookies
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <>
      <AnimatePresence>
        {showBanner && (
          <Banner
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
          >
            <CloseButton onClick={acceptNecessary}>
              <X size={20} />
            </CloseButton>
            <Container>
              <IconWrapper>
                <Cookie size={28} />
              </IconWrapper>
              
              <Content>
                <Title>We Value Your Privacy</Title>
                <Description>
                  We use cookies to enhance your browsing experience, analyze site traffic, 
                  and personalize content. By clicking "Accept All", you consent to our use of cookies.{' '}
                  <Link to="/privacy-policy">Learn more</Link>
                </Description>
              </Content>

              <Actions>
                <Button onClick={() => setShowModal(true)}>
                  <Settings size={18} />
                  Preferences
                </Button>
                <Button onClick={acceptNecessary}>
                  Necessary Only
                </Button>
                <Button $primary onClick={acceptAll}>
                  <Check size={18} />
                  Accept All
                </Button>
              </Actions>
            </Container>
          </Banner>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showModal && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowModal(false)}
          >
            <Modal
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHeader>
                <ModalTitle>Cookie Preferences</ModalTitle>
                <ModalSubtitle>
                  Choose which cookies you'd like to accept
                </ModalSubtitle>
                <ModalClose onClick={() => setShowModal(false)}>
                  <X size={20} />
                </ModalClose>
              </ModalHeader>

              <ModalBody>
                <PreferenceItem>
                  <PreferenceHeader>
                    <PreferenceTitle>Necessary Cookies</PreferenceTitle>
                    <Toggle $active={preferences.necessary} $disabled>
                      <span className="sr-only">Always enabled</span>
                    </Toggle>
                  </PreferenceHeader>
                  <PreferenceDescription>
                    These cookies are essential for the website to function properly. 
                    They enable basic features like page navigation and access to secure areas.
                  </PreferenceDescription>
                </PreferenceItem>

                <PreferenceItem>
                  <PreferenceHeader>
                    <PreferenceTitle>Analytics Cookies</PreferenceTitle>
                    <Toggle 
                      $active={preferences.analytics}
                      onClick={() => togglePreference('analytics')}
                    >
                      <span className="sr-only">Toggle analytics cookies</span>
                    </Toggle>
                  </PreferenceHeader>
                  <PreferenceDescription>
                    These cookies help us understand how visitors interact with our website, 
                    allowing us to improve our services and user experience.
                  </PreferenceDescription>
                </PreferenceItem>

                <PreferenceItem>
                  <PreferenceHeader>
                    <PreferenceTitle>Marketing Cookies</PreferenceTitle>
                    <Toggle 
                      $active={preferences.marketing}
                      onClick={() => togglePreference('marketing')}
                    >
                      <span className="sr-only">Toggle marketing cookies</span>
                    </Toggle>
                  </PreferenceHeader>
                  <PreferenceDescription>
                    These cookies are used to deliver personalized advertisements and measure 
                    the effectiveness of our marketing campaigns.
                  </PreferenceDescription>
                </PreferenceItem>
              </ModalBody>

              <ModalFooter>
                <Button onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button $primary onClick={savePreferences}>
                  <Check size={18} />
                  Save Preferences
                </Button>
              </ModalFooter>
            </Modal>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </>
  );
};

export default CookieConsent;